import type { AuthState, ModuleSettings } from './state'
import type { UserRole, Tenant } from '@/types'

export const getters = {
  isSuperAdmin: (state: AuthState): boolean => {
    return state.user?.role === 'superadmin'
  },

  currentTenant: (state: AuthState): Tenant | null => {
    return state.currentTenant
  },

  tenantFeatures: (state: AuthState): string[] => {
    return state.currentTenant?.features || []
  },

  isImpersonating: (state: AuthState): boolean => {
    return state.impersonatingTenantId !== null
  },

  effectiveTenantId: (state: AuthState): string | null => {
    return state.impersonatingTenantId || state.user?.tenantId || null
  },

  isTenantAdmin: (state: AuthState): boolean => {
    return state.user?.role === 'tenant_admin'
  },

  isAdmin: (state: AuthState): boolean => {
    return state.user?.role === 'admin'
  },

  isOperator: (state: AuthState): boolean => {
    return state.user?.role === 'operator'
  },

  isClient: (state: AuthState): boolean => {
    return state.user?.role === 'client'
  },

  isMember: (state: AuthState): boolean => {
    return state.user?.role === 'member'
  },

  isOrganizationOwner: (state: AuthState): boolean => {
    return state.user?.isOrganizationOwner === true
  },

  userRole: (state: AuthState): UserRole | null => {
    return state.user?.role ?? null
  },

  userId: (state: AuthState): string | null => {
    return state.user?.id ?? null
  },

  userTenantId: (state: AuthState): string | null => {
    return state.user?.tenantId ?? null
  },

  userOrganizationId: (state: AuthState): string | null => {
    return state.user?.organizationId ?? null
  },

  operatorOrganizationIds: (state: AuthState): string[] => {
    if (state.user?.role !== 'operator') return []
    return state.user.assignedOrganizationIds || []
  },

  userDisplayName: (state: AuthState): string => {
    return state.user?.displayName ?? ''
  },

  userEmail: (state: AuthState): string => {
    return state.user?.email ?? ''
  },

  userLanguage: (state: AuthState): string => {
    return state.user?.language ?? 'es'
  },

  userTheme: (state: AuthState): string => {
    return state.user?.theme ?? 'light'
  },

  canManageUsers: (state: AuthState): boolean => {
    const role = state.user?.role
    return role === 'superadmin' || role === 'tenant_admin' || role === 'admin'
  },

  canManageOrganizations: (state: AuthState): boolean => {
    const role = state.user?.role
    return role === 'superadmin' || role === 'tenant_admin' || role === 'admin'
  },

  canManageCRM: (state: AuthState): boolean => {
    const role = state.user?.role
    return role === 'superadmin' || role === 'tenant_admin' || role === 'admin'
  },

  canManageSalesOrders: (state: AuthState): boolean => {
    const role = state.user?.role
    return role === 'superadmin' || role === 'tenant_admin' || role === 'admin'
  },

  canManageSettings: (state: AuthState): boolean => {
    const role = state.user?.role
    return role === 'superadmin' || role === 'tenant_admin' || role === 'admin'
  },

  canCreateProjects: (state: AuthState): boolean => {
    const role = state.user?.role
    return role === 'superadmin' || role === 'tenant_admin' || role === 'admin'
  },

  canDeleteProjects: (state: AuthState): boolean => {
    const role = state.user?.role
    return role === 'superadmin' || role === 'tenant_admin' || role === 'admin'
  },

  canAssignOperators: (state: AuthState): boolean => {
    const role = state.user?.role
    return role === 'superadmin' || role === 'tenant_admin' || role === 'admin'
  },

  canManageTenants: (state: AuthState): boolean => {
    return state.user?.role === 'superadmin'
  },

  canManageOrganizationMembers: (state: AuthState): boolean => {
    const role = state.user?.role
    return (
      (role === 'client' && state.user?.isOrganizationOwner === true) ||
      role === 'superadmin' || role === 'tenant_admin' || role === 'admin'
    )
  },

  hasInternalAccess: (state: AuthState): boolean => {
    const role = state.user?.role
    return role === 'superadmin' || role === 'tenant_admin' || role === 'admin' || role === 'operator'
  },

  moduleSettings: (state: AuthState): ModuleSettings => {
    return state.moduleSettings
  },

  isModuleEnabled: (state: AuthState) => (module: keyof ModuleSettings): boolean => {
    return state.moduleSettings[module] === true
  },
}
